// src/components/inventory/BulkUpload.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dbHelpers } from '../../db/database';
import apiClient from '../../api/client';
import syncService from '../../services/syncService';
import Papa from 'papaparse';
import './BulkUpload.css';

function BulkUpload() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  function handleFileChange(e) {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    
    Papa.parse(selectedFile, {
      header: true,
      preview: 5,
      skipEmptyLines: true,
      complete: (results) => {
        setPreview(results.data);
      },
      error: (error) => {
        alert('Failed to parse CSV: ' + error.message);
      }
    });
  }

  async function handleUpload() {
    if (!file) return;

    if (!navigator.onLine) {
      alert('⚠️ You are offline. Bulk upload requires internet connection.');
      return;
    }

    setUploading(true);
    setErrors([]);
    setProgress({ current: 0, total: 0 });

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const products = results.data.filter(p => p.name && p.name.trim());
        
        if (products.length === 0) {
          alert('❌ No valid products found in CSV. Make sure you have a "name" column.');
          setUploading(false);
          return;
        }

        const uploadErrors = [];
        let successCount = 0;
        setProgress({ current: 0, total: products.length });

        for (let i = 0; i < products.length; i++) {
          const product = products[i];
          
          setProgress({ current: i + 1, total: products.length });

          try {
            // const productData = {
            //   name: product.name.trim(),
            //   sku: product.sku ? product.sku.trim().toUpperCase() : '',
            //   category: product.category?.trim() || 'Other',
            //   buying_price: parseFloat(product.buying_price || 0),
            //   selling_price: parseFloat(product.selling_price || 0),
            //   description: product.description?.trim() || '',
            //   is_active: true,
            // };
            const productData = {
              name: product.name.trim(),
              category: product.category?.trim() || 'Other',
              buying_price: parseFloat(product.buying_price || 0),
              selling_price: parseFloat(product.selling_price || 0),
              description: product.description?.trim() || '',
              is_active: true
            };

            if (product.sku?.trim()) {
              productData.sku = product.sku.trim().toUpperCase();
            }
            const response = await apiClient.post('/products/', productData);
            await dbHelpers.saveProducts([response.data]);
            
            successCount++;
          } catch (error) {
            console.error(`Row ${i + 1} failed:`, error);
            
            let errorMsg = 'Unknown error';
            
            if (error.response?.data) {
              const errorData = error.response.data;
              
              if (errorData.sku) {
                errorMsg = Array.isArray(errorData.sku) ? errorData.sku[0] : errorData.sku;
              } else if (errorData.name) {
                errorMsg = Array.isArray(errorData.name) ? errorData.name[0] : errorData.name;
              } else if (errorData.detail) {
                errorMsg = errorData.detail;
              } else if (errorData.non_field_errors) {
                errorMsg = errorData.non_field_errors[0];
              } else {
                errorMsg = JSON.stringify(errorData);
              }
            } else {
              errorMsg = error.message;
            }
            
            uploadErrors.push(`Row ${i + 1} (${product.name}): ${errorMsg}`);
          }

          if (i % 10 === 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        await syncService.fullSync();
        setUploading(false);
        setErrors(uploadErrors);

        if (uploadErrors.length === 0) {
          alert(`✅ Successfully uploaded ${successCount} products!`);
          navigate('/inventory');
        } else {
          alert(
            `Upload Complete:\n\n` +
            `✅ Success: ${successCount}\n` +
            `❌ Failed: ${uploadErrors.length}\n\n` +
            `See details below.`
          );
        }
      },
      error: (error) => {
        setUploading(false);
        alert('❌ Failed to parse CSV file: ' + error.message);
      }
    });
  }

  function downloadTemplate() {
    // ⭐ NO SKU COLUMN - will auto-generate
    const template = `name,category,buying_price,selling_price,description
    Argan Oil,Cosmetics,31.50,52.91,Authentic argan oil from Morocco
    Black Soap,Cosmetics,39.24,71.59,Traditional African black soap
    Cotton Fabric,Textiles,25.00,45.00,100% organic cotton fabric`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }


  return (
    <div className="bulk-upload-container fade-in">
      <div className="breadcrumb">
        <button onClick={() => navigate('/inventory')} className="btn-primary-small">
          ← Back to Inventory
        </button>
      </div>

      <div className="form-card">
        <h1>📤 Bulk Upload Products</h1>
        <p className="subtitle">Upload a CSV file to import multiple products at once</p>

        <div className="upload-section">
          <div className="template-download">
            <p>📋 Don't have a CSV file? Download our template:</p>
            <button onClick={downloadTemplate} className="btn-secondary">
              📥 Download Template
            </button>
            <p style={{fontSize: '0.9em', color: '#666', marginTop: '8px'}}>
              💡 SKU will be auto-generated for all products
            </p>
          </div>

          <div className="file-upload">
            <label className="file-input-label">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={uploading}
                style={{display: 'none'}}
              />
              <div className="file-dropzone">
                {file ? (
                  <>
                    <div className="file-icon">📄</div>
                    <p className="file-name">{file.name}</p>
                    <p className="file-size">{(file.size / 1024).toFixed(2)} KB</p>
                  </>
                ) : (
                  <>
                    <div className="upload-icon">☁️</div>
                    <p>Click to select CSV file</p>
                    <p className="hint">Maximum file size: 5MB</p>
                  </>
                )}
              </div>
            </label>
          </div>

          {preview.length > 0 && (
            <div className="preview-section">
              <h3>📊 Preview (first 5 rows)</h3>
              <div className="preview-table-wrapper">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      {preview[0]?.sku && <th>SKU</th>}
                      <th>Category</th>
                      <th>Buying Price</th>
                      <th>Selling Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i}>
                        <td>{row.name || '—'}</td>
                        {preview[0]?.sku && <td>{row.sku || 'Auto-generate'}</td>}
                        <td>{row.category || 'Other'}</td>
                        <td>TSh {row.buying_price || '0.00'}</td>
                        <td>TSh {row.selling_price || '0.00'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{fontSize: '0.9em', color: '#666', marginTop: '10px'}}>
                💡 SKU will be auto-generated as: ORG-CAT-001-PRODUCTNAME
              </p>
            </div>
          )}

          {uploading && (
            <div className="progress-section">
              <p className="progress-text">
                Uploading: <strong>{progress.current}</strong> / {progress.total}
              </p>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{width: `${(progress.current / progress.total) * 100}%`}}
                />
              </div>
              <p className="progress-hint">Please don't close this page...</p>
            </div>
          )}

          {errors.length > 0 && (
            <div className="errors-section">
              <h3>❌ Errors ({errors.length})</h3>
              <div className="error-list-wrapper">
                <ul className="error-list">
                  {errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="button-group">
            <button 
              onClick={() => navigate('/inventory')} 
              className="btn btn-secondary"
              disabled={uploading}
            >
              Cancel
            </button>
            <button 
              onClick={handleUpload} 
              className="btn btn-primary"
              disabled={!file || uploading}
            >
              {uploading 
                ? `Uploading... (${progress.current}/${progress.total})` 
                : '📤 Upload Products'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkUpload;
